import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { formatCurrency } from '@/lib/formatUtils';
import type { PerformanceSnapshot } from '@/types/dashboard';
import type { MacroKPIs } from '@/types/dashboard';

interface PerformanceSnapshotCardProps {
  data: PerformanceSnapshot;
  macroKPIs?: MacroKPIs;
  salesBasis?: 'gross' | 'net';
}

export const PerformanceSnapshotCard: React.FC<PerformanceSnapshotCardProps> = ({
  data,
  macroKPIs,
  salesBasis = 'net',
}) => {
  const TrendIcon = ({ value }: { value: number }) => {
    if (value >= 0) {
      return <TrendingUp className="h-4 w-4 text-emerald-600" />;
    }
    return <TrendingDown className="h-4 w-4 text-amber-600" />;
  };

  return (
    <Card className="bg-card">
      <CardContent className="p-6">
        <div className="mb-6">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Performance Snapshot
          </span>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-4">
          <div>
            <p className="text-sm text-muted-foreground mb-1">
              {salesBasis === 'gross' ? 'Gross Sales (Incl. Tax)' : 'Gross Sales (Excl. Tax)'}
            </p>
            <p className="text-3xl font-bold tracking-tight font-mono">
              {formatCurrency(salesBasis === 'gross' ? data.whatDidISell * 1.0825 : data.whatDidISell)}
            </p>
            <div className="flex items-center gap-1 mt-1">
              <TrendIcon value={data.sellChange} />
              <span className={`text-sm font-medium ${
                data.sellChange >= 0 ? 'text-emerald-600' : 'text-amber-600'
              }`}>
                {data.sellChange >= 0 ? '+' : ''}{data.sellChange.toFixed(1)}%
              </span>
            </div>
          </div>

          <div>
            <p className="text-sm text-muted-foreground mb-1">Net Profit</p>
            <p className={`text-3xl font-bold tracking-tight font-mono ${
              data.whatDidIMake < 0 ? 'text-red-600' : ''
            }`}>
              {data.whatDidIMake < 0 ? `(${formatCurrency(Math.abs(data.whatDidIMake))})` : formatCurrency(data.whatDidIMake)}
            </p>
            <div className="flex items-center gap-1 mt-1">
              <TrendIcon value={data.makeChange} />
              <span className={`text-sm font-medium ${
                data.makeChange >= 0 ? 'text-emerald-600' : 'text-amber-600'
              }`}>
                {data.makeChange >= 0 ? '+' : ''}{data.makeChange.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        {macroKPIs && (
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="rounded-md bg-muted/50 px-3 py-2">
              <p className="text-xs text-muted-foreground">Tickets</p>
              <p className="text-lg font-semibold font-mono">{macroKPIs.tickets.toLocaleString()}</p>
            </div>
            <div className="rounded-md bg-muted/50 px-3 py-2">
              <p className="text-xs text-muted-foreground">Avg Ticket Price</p>
              <p className="text-lg font-semibold font-mono">{formatCurrency(macroKPIs.avgTicketPrice)}</p>
            </div>
          </div>
        )}

        <div className="border-t border-border my-4" />

        <p className="text-sm text-muted-foreground mb-2">
          {data.comparisonText}
        </p>

        <p className="text-sm text-muted-foreground">
          {data.insight}
        </p>
      </CardContent>
    </Card>
  );
};
